/**
 * BackendService - Main service layer for boardgame tracker
 * Coordinates between BGG API and PocketBase database
 */

import { searchBoardgames, getBoardgameDetails } from './BGGAPIClient.js';
import DatabaseRepository from './DatabaseRepository.js';
import { ProxyClient, fetchWithRetry, CatalogCache } from '@code-burrow/tracker-core';

export class BackendService {
    constructor(getToken) {
        const pocketbaseURL = import.meta.env.VITE_POCKETBASE_URL;
        if (!pocketbaseURL) throw new Error('VITE_POCKETBASE_URL is not set');

        this.proxy = new ProxyClient(getToken);
        this.db = new DatabaseRepository(pocketbaseURL, this.proxy);
        this._userIdCache = new Map();
    }

    get _apiBase() {
        return import.meta.env.VITE_PROXY_URL
            ? `${import.meta.env.VITE_PROXY_URL.replace(/\/+$/, '')}/api`
            : '/api';
    }

    async _authHeaders(currentUserId) {
        const headers = {};
        if (currentUserId) headers['x-tracker-user-id'] = currentUserId;
        if (this.proxy._getToken) {
            try {
                const token = await this.proxy._getToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            } catch (_) {}
        }
        return headers;
    }

    async getAggregatedCollection(currentUserId) {
        const headers = await this._authHeaders(currentUserId);
        return fetchWithRetry(`${this._apiBase}/collection/boardgames`, { headers });
    }

    async clearCatalogCache() {
        try { await new CatalogCache('boardgames').clear(); } catch {}
    }

    async getCatalog() {
        const headers = await this._authHeaders();
        return fetchWithRetry(`${this._apiBase}/collection/boardgames/catalog`, { headers });
    }

    async getUserData(currentUserId) {
        const headers = await this._authHeaders(currentUserId);
        return fetchWithRetry(`${this._apiBase}/collection/boardgames/userdata`, { headers });
    }

    // ── BGG Search ──────────────────────────────────────────────────────────────

    async searchBoardgames(query) {
        try {
            const items = await searchBoardgames(query);
            return { success: true, items };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getBoardgameDetails(bggId) {
        try {
            const details = await getBoardgameDetails(bggId);
            return { success: true, details };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // ── Collection CRUD ─────────────────────────────────────────────────────────

    async getAllBoardgames() {
        try {
            const boardgames = await this.db.getAllBoardgames();
            return { success: true, boardgames };
        } catch (error) {
            if (error.message?.includes('autocancelled')) return { success: true, boardgames: [] };
            return { success: false, error: error.message };
        }
    }

    async getAllUserBoardgames() {
        try {
            const records = await this.db.pb.collection('user_boardgames').getFullList({
                $autoCancel: false,
                batch: 1000,
            });
            return { success: true, records };
        } catch {
            return { success: true, records: [] };
        }
    }

    async addBoardgame(data) {
        try {
            // Check if already in collection
            const existing = await this.db.findByBggId(data.bggId);
            if (existing) return { success: true, boardgame: existing, alreadyExists: true };

            const record = await this.db.createBoardgame(data);
            return { success: true, boardgame: record };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async updateBoardgame(id, updates) {
        try {
            await this.db.updateBoardgame(id, updates);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async deleteBoardgame(id) {
        try {
            await this.db.deleteBoardgame(id);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // ── Join table CRUD (user_boardgames) ────────────────────────────────────────

    async resolveUserId(userName) {
        if (this._userIdCache.has(userName)) return this._userIdCache.get(userName);
        const record = await this.db.pb.collection('tracker_users')
            .getFirstListItem(`name="${userName}"`, { $autoCancel: false });
        this._userIdCache.set(userName, record.id);
        return record.id;
    }

    async upsertUserBoardgame(userName, boardgamePbId, data) {
        const userId = await this.resolveUserId(userName);
        return this.proxy.collection('user_boardgames').upsert({ boardgame: boardgamePbId }, data, userId);
    }

    async deleteUserBoardgame(userName, boardgamePbId) {
        const userId = await this.resolveUserId(userName);
        const record = await this.db.pb.collection('user_boardgames')
            .getFirstListItem(`user="${userId}" && boardgame="${boardgamePbId}"`, { $autoCancel: false });
        await this.proxy.collection('user_boardgames').delete(record.id);
        return true;
    }

    // ── Lists CRUD ──────────────────────────────────────────────────────────────

    async getAllLists(_userName) {
        try {
            const headers = await this._authHeaders();
            const res = await fetch(`${this._apiBase}/pb/lists?app=boardgames`, { headers });
            if (!res.ok) {
                console.error('getAllLists failed:', res.status);
                return { success: false, lists: [] };
            }
            return res.json();
        } catch (error) {
            console.error('getAllLists error:', error);
            return { success: false, lists: [] };
        }
    }

    async createList(_userName, { name, emoji }) {
        // Server resolves the user from the Clerk JWT — no need to pass userId from client
        return this.proxy.collection('lists').create({
            name, emoji: emoji || '', app: 'boardgames',
        });
    }

    async updateList(listId, data) {
        return this.proxy.collection('lists').update(listId, data);
    }

    async deleteList(listId) {
        // Delete all items in the list first (via proxy — list_items is also locked)
        const itemsResult = await this.getListItems(listId);
        for (const item of itemsResult.items || []) {
            await this.proxy.collection('list_items').delete(item.id);
        }
        await this.proxy.collection('lists').delete(listId);
    }

    // ── List Items CRUD ─────────────────────────────────────────────────────────

    async getListItems(listId) {
        try {
            const headers = await this._authHeaders();
            const res = await fetch(`${this._apiBase}/pb/list-items?list=${encodeURIComponent(listId)}`, { headers });
            if (!res.ok) {
                console.error('getListItems failed:', res.status);
                return { success: false, items: [] };
            }
            return res.json();
        } catch (error) {
            console.error('getListItems error:', error);
            return { success: false, items: [] };
        }
    }

    async addListItem(listId, { itemId, title, imageURL, notes }) {
        // Get next position via proxy (list_items is locked to direct browser reads)
        const existing = await this.getListItems(listId);
        const items = existing.items || [];
        const nextPos = items.length > 0 ? (items[items.length - 1].position || 0) + 1 : 0;

        return this.proxy.collection('list_items').create({
            list: listId,
            itemId: itemId || '',
            title,
            imageURL: imageURL || '',
            notes: notes || '',
            position: nextPos,
        });
    }

    async updateListItem(id, data) {
        return this.proxy.collection('list_items').update(id, data);
    }

    async deleteListItem(id) {
        await this.proxy.collection('list_items').delete(id);
    }

    /**
     * Promote an unlinked list item to the collection.
     * Searches BGG, adds the boardgame, and links the list item.
     */
    async promoteListItem(listItemId, bggData) {
        const result = await this.addBoardgame(bggData);
        if (!result.success) throw new Error(result.error);
        await this.proxy.collection('list_items').update(listItemId, {
            itemId: result.boardgame.id,
        });
        return result.boardgame;
    }
}

export default BackendService;
